// Team Schedule display helpers (031) — mirrors utils/teamClassification.js's
// role as the one centralized place for formatting, so pages never
// re-derive the same date/time-parsing logic independently.
//
// event_date/start_time/end_time come back from Postgres as plain
// 'YYYY-MM-DD' / 'HH:MM:SS' strings (columns are `date`/`time`, no time
// zone — see migration 030's own comments for why). Never pass
// event_date through `new Date(dateString)` directly — JS parses a bare
// ISO date string as UTC midnight, which can display as the PREVIOUS day
// in any timezone behind UTC. parseLocalDate() below is the one place
// that conversion happens, always via explicit year/month/day
// components in the browser's own local time, never through UTC.

export const EVENT_TYPE_LABELS = {
  game: 'Game',
  practice: 'Practice',
  other: 'Other',
};

export const HOME_AWAY_LABELS = {
  home: 'Home',
  away: 'Away',
  neutral: 'Neutral',
};

function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** "Sat, Sep 12" */
export function formatEventDate(dateStr) {
  return parseLocalDate(dateStr).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatClockTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { text: `${h12}:${String(m).padStart(2, '0')}`, period };
}

/** "10:00 AM" or, with an end time in the same period, "5:30–7:00 PM". */
export function formatEventTimeRange(startTime, endTime) {
  const start = formatClockTime(startTime);
  if (!endTime) return `${start.text} ${start.period}`;
  const end = formatClockTime(endTime);
  if (start.period === end.period) return `${start.text}–${end.text} ${end.period}`;
  return `${start.text} ${start.period}–${end.text} ${end.period}`;
}

/** "5:00 PM" — a single time, 12-hour, for compact schedule rows (the
 * Division Hub) that previously showed the raw 24-hour stored value
 * (e.g. "17:00"). This audience reads a football schedule in normal
 * 12-hour clock time, never military time. */
export function formatTimeCompact(timeStr) {
  const { text, period } = formatClockTime(timeStr);
  return `${text} ${period}`;
}

/** Today's date as a 'YYYY-MM-DD' string in the viewer's own local time —
 * the same shape event_date comes back as, so Upcoming/Past can compare
 * as plain strings with no Date-object timezone risk at all. */
export function todayDateString() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** The current wall-clock time as 'HH:MM' (24h, zero-padded) in the
 * viewer's own local time — the same shape start_time/end_time already
 * come back as, so a same-day cutoff can compare as plain strings with
 * no Date-object/timezone conversion at all (same philosophy as
 * todayDateString() above). */
export function nowTimeString() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** BROWSER ACCEPTANCE FIX (C.5e) — SAME-DAY UPCOMING/PAST. Both
 * TeamSchedulePage.js's own Upcoming/Past split and DivisionHub's Up
 * Next/Scheduled sections previously compared ONLY `event_date` against
 * today, with no time-of-day awareness at all — an event that ran
 * 5:00-6:40 PM still read as "Upcoming" at 10 PM the same day. This is
 * the one shared rule both surfaces now apply:
 *   - a future date is always upcoming; a past date is always historical
 *   - on the event's own date: upcoming until end_time (if present),
 *     else upcoming until start_time, else remains date-only (treated
 *     as upcoming all day — every event actually has a start_time, so
 *     this branch is defensive, never really reached)
 * Deliberately NOT an "in progress"/NOW state (not asked for this
 * phase) — a currently-happening event simply still reads as
 * "upcoming" right up until its own cutoff.
 *
 * NEEDS RESULT IS A SEPARATE, calendar-date-only concept and must NEVER
 * use this function — see divisionSchedule.js's deriveNeedsResult()
 * for why (a Game must not become "needs a result" merely because
 * today's kickoff time passed; only a full calendar day with no result
 * qualifies). */
export function isEventUpcoming(event, { todayStr, nowTimeStr } = {}) {
  const today = todayStr || todayDateString();
  if (event.event_date > today) return true;
  if (event.event_date < today) return false;
  const cutoff = (event.end_time || event.start_time || '').slice(0, 5);
  if (!cutoff) return true;
  const nowTime = nowTimeStr || nowTimeString();
  return nowTime < cutoff;
}

/** "Fort Atkinson, WI 53538" (or whatever subset is present — city only,
 * state only, zip only, any combination). Never a dangling comma/space
 * for a missing piece. */
function formatCityStateZip(event) {
  const cityState = [event.city, event.state].filter(Boolean).join(', ');
  return [cityState, event.zip_code].filter(Boolean).join(' ');
}

/** Up to two display lines for an event's location — location_name (a
 * venue name) on its own line, and the actual street address ("123
 * Example St, Fort Atkinson, WI 53538") on a second line, so a saved
 * address_line1 is never silently dropped the way it previously was.
 * Returns an array of 0, 1, or 2 non-empty strings — callers render
 * whatever's there and nothing when the array is empty, never a
 * placeholder line. Every field is optional and independently omitted:
 *   name + full address  -> ["Memorial Park Football Fields", "123 Example St, Fort Atkinson, WI 53538"]
 *   name + city/state     -> ["Memorial Park Football Fields", "Fort Atkinson, WI"]
 *   address, no name      -> ["123 Example St, Fort Atkinson, WI 53538"]
 *   name only              -> ["Memorial Park Football Fields"]
 *   city/state only        -> ["Fort Atkinson, WI"]
 *   nothing                -> [] */
export function formatEventLocationLines(event) {
  const cityStateZip = formatCityStateZip(event);
  const address = [event.address_line1, cityStateZip].filter(Boolean).join(', ');
  return [event.location_name, address].filter(Boolean);
}
